import { auth0ApiClient } from './auth0ApiClient'
import { GroupRoom, CreateGroupRequest, AddGroupMembersRequest } from '@/types/group'

class GroupService {
  private readonly baseUrl = '/groups'

  /**
   * Create a new group room
   */
  async createGroup(data: CreateGroupRequest): Promise<GroupRoom> {
    return auth0ApiClient.post<GroupRoom>(this.baseUrl, data)
  }

  /**
   * Get all group rooms the current user belongs to
   */
  async getGroups(): Promise<GroupRoom[]> {
    return auth0ApiClient.get<GroupRoom[]>(this.baseUrl)
  }

  /**
   * Find or create a direct (1:1) conversation with the given user
   */
  async createDirect(userId: string): Promise<GroupRoom> {
    return auth0ApiClient.post<GroupRoom>(`${this.baseUrl}/direct`, { userId })
  }

  /**
   * Get a single group room's details and member list
   */
  async getGroup(roomId: string): Promise<GroupRoom> {
    return auth0ApiClient.get<GroupRoom>(`${this.baseUrl}/${roomId}`)
  }

  /**
   * Add members to a group room (room admin only)
   */
  async addMembers(roomId: string, data: AddGroupMembersRequest): Promise<GroupRoom> {
    return auth0ApiClient.post<GroupRoom>(`${this.baseUrl}/${roomId}/members`, data)
  }

  /**
   * Remove a member from a group room. Usable by a room admin removing
   * someone else, or by any member removing themselves (leave).
   */
  async removeMember(roomId: string, userId: string): Promise<{ roomId: string; userId: string }> {
    return auth0ApiClient.delete<{ roomId: string; userId: string }>(
      `${this.baseUrl}/${roomId}/members/${userId}`
    )
  }

  /**
   * Dissolve a group room (room admin only). Deactivates the room and
   * removes all memberships server-side.
   */
  async dissolveGroup(roomId: string): Promise<{ roomId: string }> {
    return auth0ApiClient.delete<{ roomId: string }>(`${this.baseUrl}/${roomId}`)
  }
}

export const groupService = new GroupService()
